import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

// TODO: (nachiket) When migrating to use shared libs instead of forked ones, replace these usages with the correct VDS tokens.
import { colors } from '@verantos-dev/shared-ui-components/tokens';

import { TEST_BATCH_TYPE } from '../../constants';
import { formatBatchTypeLabel } from '../../emitters/formatBatchTypeLabel';
import type { AccuracyBatchOption } from '../../types/response';

interface BatchCardProps {
  batch: AccuracyBatchOption;
  selected: boolean;
  onSelect: (batch: AccuracyBatchOption) => void;
}

export const BatchCard = ({ batch, selected, onSelect }: BatchCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const locked = !batch.isSelectableForAccuracy;

  // Each encounter is annotated twice (two annotators per encounter), so the
  // completed-allocation count is halved to get the number of completed encounters.
  const encountersCompleted = Math.floor(batch.annotationCompletedCount / 2);

  const card = (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={locked}
      tabIndex={locked ? -1 : 0}
      onClick={() => !locked && onSelect(batch)}
      onKeyDown={(event) => {
        if (!locked && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect(batch);
        }
      }}
      sx={{
        flex: 1,
        p: theme.spacing(2),
        borderRadius: 1,
        cursor: locked ? 'not-allowed' : 'pointer',
        border: `${selected ? '2px' : '1px'} solid ${
          selected
            ? batch.batchType === TEST_BATCH_TYPE
              ? colors['warning-500']
              : theme.palette.primary.main
            : theme.palette.divider
        }`,
        backgroundColor: selected
          ? batch.batchType === TEST_BATCH_TYPE
            ? colors['warning-50']
            : theme.palette.action.selected
          : locked
          ? theme.palette.grey[50]
          : theme.palette.background.paper,
        opacity: locked ? 0.7 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1.5) }}>
        {/* Selection / lock icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            alignSelf: 'stretch',
          }}
        >
          {locked ? (
            <LockOutlinedIcon
              sx={{
                color: theme.palette.text.disabled,
                fontSize: 24,
              }}
            />
          ) : selected ? (
            <RadioButtonCheckedIcon
              sx={{
                color:
                  batch.batchType === TEST_BATCH_TYPE
                    ? colors['warning-600']
                    : theme.palette.primary.main,
                fontSize: 24,
              }}
            />
          ) : (
            <RadioButtonUncheckedIcon
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 24,
              }}
            />
          )}
        </Box>

        {/* Card content */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {formatBatchTypeLabel(batch.batchType, formatMessage)}
            </Typography>

            {batch.batchType === TEST_BATCH_TYPE && (
              <Chip
                variant="outlined"
                size="small"
                icon={<WarningAmberIcon />}
                sx={{
                  backgroundColor: colors['warning-50'],
                  color: colors['warning-600'],
                  '& .MuiChip-icon': {
                    color: colors['warning-600'],
                    fontSize: 18,
                  },
                  fontSize: '10px',
                  fontWeight: 700,
                  borderColor: colors['warning-600'],
                }}
                label={formatMessage({
                  id: 'adminProjects.generateAccuracy.batch.finalEval',
                })}
              />
            )}
          </Box>

          {locked ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatMessage(
                  { id: 'adminProjects.generateAccuracy.batch.completedCount' },
                  {
                    completed: encountersCompleted,
                    total: batch.visitCount,
                  }
                )}
              </Typography>

              <LinearProgress
                variant="determinate"
                value={batch.visitCount > 0 ? (encountersCompleted / batch.visitCount) * 100 : 0}
                sx={{ mt: 1 }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatMessage(
                { id: 'adminProjects.generateAccuracy.batch.encounterCount' },
                { count: batch.visitCount }
              )}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  if (locked && batch.lockReason) {
    return <Tooltip title={batch.lockReason}>{card}</Tooltip>;
  }

  return card;
};
